import React, { useState, useEffect } from 'react';
import { useBandeau } from '../BandeauContext';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import { apiClient } from '../../utils/apiClient';

// Calcule la durée pour les événements récurrents.
const calculateDuration = (start, end) => {
    const durationMs = new Date(end).getTime() - new Date(start).getTime();
    const hours = String(Math.floor(durationMs / 3600000)).padStart(2, '0');
    const minutes = String(Math.floor((durationMs % 3600000) / 60000)).padStart(2, '0');
    return `${hours}:${minutes}`;
};

/**
 * Transforme les événements bruts de la BDD (avec parents et exceptions)
 * en un format que FullCalendar peut interpréter.
 */
const processRawEvents = (rawEvents) => {
    const eventMap = new Map(rawEvents.map(e => [e.id, { ...e }]));
    const calendarEvents = [];

    // 1ère passe : On traite les exceptions pour modifier leurs parents.
    for (const event of rawEvents) {
        if (event.parent_event_id) {
            const parent = eventMap.get(event.parent_event_id);
            if (parent) {
                if (!parent.exdate) parent.exdate = [];
                // On ajoute la date originale de l'occurrence à la liste d'exceptions du parent.
                // Compatibilité Safari : remplacer l'espace par un T
                const safeOriginalTime = event.original_start_time.replace(' ', 'T');
                parent.exdate.push(new Date(safeOriginalTime).toISOString());

                // Si ce n'est pas une annulation, on ajoute l'événement modifié comme un événement unique.
                if (event.is_cancelled === 0 || event.is_cancelled === '0' || !event.is_cancelled) {
                    calendarEvents.push({
                        id: event.id,
                        title: event.title,
                        start: event.start_time.replace(' ', 'T'),
                        end: event.end_time.replace(' ', 'T'),
                        color: '#eab308' // Jaune pour les événements déplacés
                    });
                }
            }
        }
    }

    // 2ème passe : On ajoute les événements récurrents (maintenant avec leurs exceptions) et les événements uniques.
    for (const event of eventMap.values()) {
        if (event.parent_event_id) continue; // Déjà traité.

        if (event.is_recurring === 1 || event.is_recurring === '1' || event.is_recurring === true) {
            const safeStartTime = event.start_time.replace(' ', 'T');
            const dtstart = new Date(safeStartTime).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            
            let rruleStr = `DTSTART:${dtstart}\nRRULE:${event.recurrence_rule}`;
            
            // FullCalendar lit nativement EXDATE s'il est inclus dans la chaîne rrule
            if (event.exdate && event.exdate.length > 0) {
                const formattedExdates = event.exdate.map(d => d.replace(/[-:]/g, '').split('.')[0] + 'Z').join(',');
                rruleStr += `\nEXDATE:${formattedExdates}`;
            }

            calendarEvents.push({
                id: event.id,
                title: event.title,
                rrule: rruleStr,
                duration: calculateDuration(event.start_time, event.end_time),
                color: '#8b5cf6' // Violet pour les révisions récurrentes
            });
        } else {
            // Événement simple, non récurrent.
            calendarEvents.push({
                id: event.id,
                title: event.title,
                start: event.start_time.replace(' ', 'T'),
                end: event.end_time.replace(' ', 'T'),
                color: '#06b6d4' // Cyan pour les événements uniques
            });
        }
    }
    return calendarEvents;
};

function AgendaView({ user, onExit }) {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { setBandeau, resetBandeau } = useBandeau();

    useEffect(() => {
        setBandeau({ breadcrumb: null, title: 'Agenda' });
        return () => resetBandeau();
    }, [setBandeau, resetBandeau]);

    useEffect(() => {
        setIsLoading(true);
        apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_agenda.php`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    const processedEvents = processRawEvents(data.events);
                    setEvents(processedEvents);
                }
                setIsLoading(false);
            })
            .catch(err => {
                console.error("Erreur de chargement de l'agenda:", err);
                setIsLoading(false);
            });
    }, []);

    const handleEventClick = (clickInfo) => {
        // TODO: Ouvrir une modale pour voir/modifier/supprimer l'événement.
        alert(`Événement : ${clickInfo.event.title}`);
    };

    return (
        <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[80vh]">
            <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-3xl p-4 md:p-8 border border-gray-200 dark:border-white/[0.05] shadow-lg [&_.fc]:text-gray-800 dark:[&_.fc]:text-gray-200 [&_.fc-button]:!bg-gray-200 dark:[&_.fc-button]:!bg-white/5 [&_.fc-button]:!text-gray-700 dark:[&_.fc-button]:!text-gray-200 [&_.fc-button]:!border-gray-300 dark:[&_.fc-button]:!border-white/10">
                {isLoading ? (
                    <div className="text-center py-20">Chargement de l'agenda...</div>
                ) : (
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,timeGridDay'
                        }}
                        initialView="dayGridMonth"
                        firstDay={1}
                        locale="fr"
                        buttonText={{ today: "Aujourd'hui", month: 'Mois', week: 'Semaine', day: 'Jour' }}
                        events={events}
                        editable={true}
                        selectable={false}
                        selectMirror={false}
                        dayMaxEvents={true}
                        eventClick={handleEventClick}
                    />
                )}
            </div>
        </div>
    );
}

export default AgendaView;